/**
 * Satellite tracking data source.
 * Fetches TLE data from CelesTrak and propagates orbital positions in real-time
 * using the satellite.js library for SGP4/SDP4 propagation.
 */

import * as satellite from 'satellite.js';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface SatellitePosition {
  id: string;
  name: string;
  lat: number;
  lng: number;
  alt: number;        // altitude in km
  country: string;
  color: string;
}

/** satellite.js SatRec with attached metadata. */
export interface SatRecord {
  satrec: satellite.SatRec;
  id: string;
  name: string;
  country: string;
  color: string;
}

// ---------------------------------------------------------------------------
// Country color mapping by NORAD designator prefix
// ---------------------------------------------------------------------------

const COUNTRY_COLORS: Record<string, string> = {
  CN: '#ff2020',
  RU: '#ff8800',
  US: '#4488ff',
  EU: '#44cc44',
  KR: '#aa66ff',
  IN: '#ff66aa',
  TR: '#ff4466',
  OTHER: '#ccccff',
};

/** Known NORAD international designator prefixes mapped to country codes. */
const DESIGNATOR_TO_COUNTRY: Record<string, string> = {
  // China
  CZ: 'CN', CN: 'CN',
  // Russia / Soviet Union
  SL: 'RU', COSMOS: 'RU',
  // European
  ARIANE: 'EU', VEGA: 'EU',
  // India
  PSLV: 'IN',
  // South Korea
  NURI: 'KR',
  // Turkey
  GOKTURK: 'TR', TURKSAT: 'TR',
};

/**
 * Infer country from satellite name using common prefixes and keywords.
 * This is a heuristic — CelesTrak does not provide country directly in TLE.
 */
function inferCountry(name: string): string {
  const upper = name.toUpperCase().trim();

  // Chinese satellites
  if (upper.startsWith('CZ-') || upper.includes('YAOGAN') || upper.includes('BEIDOU') ||
      upper.includes('FENGYUN') || upper.includes('SHIJIAN') || upper.includes('TIANLIAN') ||
      upper.includes('TIANZHOU') || upper.includes('SHENZHOU') || upper.includes('CHANG ZHENG') ||
      upper.includes('GAOFEN') || upper.includes('SHIYAN') || upper.includes('ZHONGXING')) {
    return 'CN';
  }

  // Russian satellites
  if (upper.startsWith('COSMOS') || upper.startsWith('KOSMOS') || upper.includes('GLONASS') ||
      upper.includes('MOLNIYA') || upper.includes('SOYUZ') || upper.includes('PROGRESS') ||
      upper.includes('METEOR') || upper.includes('RESURS') || upper.includes('GONETS')) {
    return 'RU';
  }

  // US satellites
  if (upper.includes('STARLINK') || upper.includes('GPS') || upper.includes('TDRS') ||
      upper.includes('GOES') || upper.includes('NOAA') || upper.includes('LANDSAT') ||
      upper.includes('FALCON') || upper.includes('ATLAS') || upper.includes('DELTA') ||
      upper.includes('CYGNUS') || upper.includes('DRAGON') || upper.includes('ONEWEB') ||
      upper.includes('IRIDIUM') || upper.includes('GLOBALSTAR')) {
    return 'US';
  }

  // European satellites
  if (upper.includes('GALILEO') || upper.includes('SENTINEL') || upper.includes('METEOSAT') ||
      upper.includes('ARIANE') || upper.includes('VEGA') || upper.includes('COPERNICUS') ||
      upper.includes('ENVISAT')) {
    return 'EU';
  }

  // Indian satellites
  if (upper.includes('PSLV') || upper.includes('GSLV') || upper.includes('CARTOSAT') ||
      upper.includes('IRNSS') || upper.includes('INSAT') || upper.includes('ASTROSAT')) {
    return 'IN';
  }

  // South Korean satellites
  if (upper.includes('KOMPSAT') || upper.includes('NURI') || upper.includes('ARIRANG')) {
    return 'KR';
  }

  // Turkish satellites
  if (upper.includes('TURKSAT') || upper.includes('GOKTURK') || upper.includes('IMECE')) {
    return 'TR';
  }

  // Check designator prefix table
  for (const [prefix, country] of Object.entries(DESIGNATOR_TO_COUNTRY)) {
    if (upper.startsWith(prefix)) return country;
  }

  return 'OTHER';
}

function getCountryColor(country: string): string {
  return COUNTRY_COLORS[country] ?? COUNTRY_COLORS.OTHER;
}

// ---------------------------------------------------------------------------
// TLE fetching and caching
// ---------------------------------------------------------------------------

const CELESTRAK_URL = 'https://celestrak.org/NORAD/elements/gp.php?GROUP=active&FORMAT=tle';
const CACHE_DURATION_MS = 6 * 60 * 60 * 1000; // 6 hours

let cachedSatRecords: SatRecord[] | null = null;
let cacheTimestamp = 0;

/**
 * Parse raw TLE text into satellite records.
 * TLE format: 3 lines per satellite (name, line1, line2).
 */
function parseTleData(tleText: string): SatRecord[] {
  const lines = tleText.trim().split('\n').map(l => l.trim()).filter(l => l.length > 0);
  const records: SatRecord[] = [];

  for (let i = 0; i + 2 < lines.length; i += 3) {
    const name = lines[i];
    const line1 = lines[i + 1];
    const line2 = lines[i + 2];

    // Validate TLE line markers
    if (!line1.startsWith('1 ') || !line2.startsWith('2 ')) continue;

    try {
      const satrec = satellite.twoline2satrec(line1, line2);
      // Extract NORAD catalog number as ID
      const noradId = line1.substring(2, 7).trim();
      const country = inferCountry(name);

      records.push({
        satrec,
        id: noradId,
        name: name.trim(),
        country,
        color: getCountryColor(country),
      });
    } catch {
      // Skip malformed TLE entries
    }
  }

  return records;
}

/**
 * Fetch TLE data from CelesTrak. Returns cached data if within 6-hour window.
 */
async function fetchTleData(): Promise<SatRecord[]> {
  const now = Date.now();
  if (cachedSatRecords && (now - cacheTimestamp) < CACHE_DURATION_MS) {
    return cachedSatRecords;
  }

  const response = await fetch(CELESTRAK_URL);
  if (!response.ok) {
    // Return stale cache if available, otherwise throw
    if (cachedSatRecords) return cachedSatRecords;
    throw new Error(`CelesTrak fetch failed: ${response.status} ${response.statusText}`);
  }

  const text = await response.text();
  cachedSatRecords = parseTleData(text);
  cacheTimestamp = now;

  return cachedSatRecords;
}

// ---------------------------------------------------------------------------
// Propagation
// ---------------------------------------------------------------------------

/**
 * Propagate satellite positions for a given time using SGP4/SDP4.
 * Converts ECI coordinates to geodetic (lat/lng/alt).
 */
export function propagatePositions(records: SatRecord[], time: Date): SatellitePosition[] {
  const gmst = satellite.gstime(time);
  const positions: SatellitePosition[] = [];

  for (const rec of records) {
    try {
      const posVel = satellite.propagate(rec.satrec, time);

      // Skip if propagation failed (returns false/null on error)
      if (!posVel || !posVel.position || typeof posVel.position === 'boolean') continue;

      const eciPos = posVel.position as satellite.EciVec3<number>;
      const geodetic = satellite.eciToGeodetic(eciPos, gmst);

      const lat = satellite.degreesLat(geodetic.latitude);
      const lng = satellite.degreesLong(geodetic.longitude);
      const alt = geodetic.height; // km

      // Skip invalid positions
      if (!isFinite(lat) || !isFinite(lng) || !isFinite(alt)) continue;

      positions.push({
        id: rec.id,
        name: rec.name,
        lat,
        lng,
        alt,
        country: rec.country,
        color: rec.color,
      });
    } catch {
      // Skip satellites that fail propagation (decayed, bad TLE, etc.)
    }
  }

  return positions;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Fetch satellite TLE data and propagate current positions.
 * TLE data is cached for 6 hours. Positions are computed for the current time.
 */
export async function fetchSatellites(): Promise<SatellitePosition[]> {
  const records = await fetchTleData();
  return propagatePositions(records, new Date());
}

/**
 * Get cached satellite records for external propagation (e.g., orbital tracks).
 * Returns null if TLE data has not been fetched yet.
 */
export function getCachedRecords(): SatRecord[] | null {
  return cachedSatRecords;
}
